import * as React from 'react'
import { Copy, Check, Trash2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useApiTokens, useCreateApiToken, useRevokeApiToken } from '@/hooks/useApiTokens'
import { requestErrorMessage } from '@/helpers/apiClient'
import {
  API_TOKEN_LIFETIMES,
  apiTokenLifetimeSchema,
  type ApiTokenLifetime,
} from '@lib/input/api-token.input'
import {
  HIGH_RISK_API_TOKEN_SCOPES,
  READ_ONLY_API_TOKEN_SCOPES,
  type ApiTokenScope,
} from '@lib/enums/api-token-scope.enum'
import { tokenScopesOf, worksAtAssignedVenues } from '@/auth/access'
import type { ApiTokenDto } from '@lib/dto/api-token.dto'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { SectionHeader } from '@/components/ui/page-header'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableActionsHead,
  TableActionsCell,
} from '@/components/ui/table'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { FormError } from '@/components/ui/form-error'
import { ErrorState } from '@/components/ui/states'
import { ApiTokenChangesDialog } from '@/components/ApiTokenChangesDialog'

/**
 * What each scope is called to the person choosing it, and what it means.
 *
 * Named for what someone recognizes rather than for the routes underneath: nobody
 * picking a scope is thinking about controllers.
 */
const SCOPE_LABELS: Record<ApiTokenScope, { title: string; detail: string }> = {
  EVENTS: { title: 'Events and plans', detail: 'Events, their plans, documents and rentals' },
  VENUES: { title: 'Venues and floor plans', detail: 'Venues, spaces, saved layouts and inventory' },
  PEOPLE: { title: 'People', detail: 'Staff and clients, their invitations and their roles' },
  VENDOR_SELF: { title: 'Your vendor profile', detail: 'Your own details, services, packages and furniture, and the requests and reviews you receive' },
  IMPORTS: { title: 'Prism imports', detail: 'Running imports and reading the import catalog' },
  FURNITURE: { title: 'Furniture catalog', detail: 'Your furniture items and arrangements' },
  VENDOR_APPROVALS: { title: 'Vendor approvals', detail: 'Which vendors may serve your venues' },
  UPLOADS: { title: 'Image uploads', detail: 'Uploading cover images and avatars' },
  ADS: { title: 'Ad numbers', detail: 'Each venue’s Google Ads numbers, to read' },
  LEAD_STATS: { title: 'Leads & pipeline', detail: 'Each venue’s pipeline and lead numbers, to read' },
}

/**
 * The same scopes, short, for the listing.
 *
 * The picker has room to explain; a table column with six siblings does not, and
 * the full titles wrapped to five lines each.
 */
const SCOPE_SHORT: Record<ApiTokenScope, string> = {
  EVENTS: 'Events',
  VENUES: 'Venues',
  PEOPLE: 'People',
  VENDOR_SELF: 'Vendor profile',
  IMPORTS: 'Imports',
  FURNITURE: 'Furniture',
  VENDOR_APPROVALS: 'Vendor approvals',
  UPLOADS: 'Uploads',
  ADS: 'Ads',
  LEAD_STATS: 'Leads & pipeline',
}

const LIFETIME_LABELS: Record<ApiTokenLifetime, string> = {
  '30d': '30 days',
  '90d': '90 days',
  '365d': 'A year',
  never: 'Never expires',
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const ms = new Date(iso).getTime()
  if (!Number.isFinite(ms)) return '—'
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** What a token is doing, as one word. */
function tokenState(token: ApiTokenDto): { label: string; tone: 'success' | 'neutral' | 'warning' } {
  if (token.revokedAt) return { label: 'Revoked', tone: 'neutral' }
  if (!token.active) return { label: 'Expired', tone: 'warning' }
  // Not "Full access": a token reaches only the scopes it was granted, and saying
  // otherwise would misdescribe the thing someone is deciding whether to trust.
  return { label: token.readOnly ? 'Read-only' : 'Active', tone: 'success' }
}

/** The scopes a token holds, written out for the listing. */
function scopeSummary(token: ApiTokenDto): string {
  // A token from before scopes existed has no grants, and "Nothing" is the honest
  // answer: it would now be refused every route it asked for.
  if (token.scopes.length === 0) return 'Nothing'
  const parts = token.scopes.map(
    (g) => `${SCOPE_SHORT[g.scope]}${g.write && !token.readOnly ? ' (write)' : ''}`,
  )
  return token.allowDestructive ? `${parts.join(', ')} + bulk delete` : parts.join(', ')
}

/**
 * The secret, once.
 *
 * It is shown here and never again — nothing stores it, so there is nothing to
 * show later. Saying so where someone can act on it is the difference between
 * copying it now and having to make a second token in a minute.
 */
function NewToken({ secret, onDone }: { secret: string; onDone: () => void }): React.ReactElement {
  const [copied, setCopied] = React.useState(false)

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard refused (no permission, insecure context) — the text is on
      // screen and selectable, so there is still a way through.
      setCopied(false)
    }
  }

  return (
    <div className="rounded-md border border-ok-ink bg-success-soft p-4">
      <p className="text-sm font-medium text-ink">Copy your token now</p>
      <p className="mt-1 text-sm text-ink-muted">
        This is the only time it can be shown. It is stored as a fingerprint, so nobody — including
        us — can read it back.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 overflow-x-auto rounded border border-line bg-surface px-3 py-2 font-mono text-xs">
          {secret}
        </code>
        <Button type="button" variant="outline" onClick={() => void copy()}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <Button type="button" variant="ghost" className="mt-3" onClick={onDone}>
        I&rsquo;ve saved it
      </Button>
    </div>
  )
}

/**
 * Personal API tokens.
 *
 * A token does whatever its owner's role does, worked out fresh on every request
 * — so this section explains the reach rather than offering permissions to set.
 * Only organization and venue admins see it at all.
 */
export function ApiTokensSection(): React.ReactElement | null {
  const { user } = useAuth()
  // Who may hold a token is decided in lib, next to which scopes their role can
  // be granted, so this cannot drift from what the API will accept.
  const mayHold = tokenScopesOf(user).length > 0
  const tokensQuery = useApiTokens(mayHold)
  const create = useCreateApiToken()
  const revoke = useRevokeApiToken()
  const [name, setName] = React.useState('')
  const [readOnly, setReadOnly] = React.useState(true)
  const [lifetime, setLifetime] = React.useState<ApiTokenLifetime>('90d')
  // Scope → may write. A scope absent from the map is not granted at all, which
  // is the same shape the API stores and saves translating between two ideas.
  const [grants, setGrants] = React.useState<Map<ApiTokenScope, boolean>>(new Map())
  const [allowDestructive, setAllowDestructive] = React.useState(false)
  const [secret, setSecret] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  // The token whose changes are open, if any.
  const [changesOf, setChangesOf] = React.useState<ApiTokenDto | null>(null)

  if (user == null || !mayHold) return null

  const tokens = tokensQuery.data?.tokens ?? []
  const offered = tokenScopesOf(user)
  const hasWrite = [...grants.values()].some((write) => write) && !readOnly

  function toggleScope(scope: ApiTokenScope): void {
    setGrants((previous) => {
      const next = new Map(previous)
      if (next.has(scope)) next.delete(scope)
      else next.set(scope, false)
      return next
    })
  }

  function setWrite(scope: ApiTokenScope, write: boolean): void {
    setGrants((previous) => new Map(previous).set(scope, write))
  }

  /** A `<select>` hands back a string; the schema is what says it is a lifetime. */
  function onLifetimeChange(value: string): void {
    const parsed = apiTokenLifetimeSchema.safeParse(value)
    if (parsed.success) setLifetime(parsed.data)
  }

  function onCreate(event: React.FormEvent): void {
    event.preventDefault()
    setError(null)
    create.mutate(
      {
        name: name.trim(),
        readOnly,
        lifetime,
        // A write flag ticked before Read-only was ticked back on would otherwise
        // be stored as a permission the token can never use — the same reason
        // allowDestructive is zeroed below.
        // And a scope that only reads has nothing to write.
        scopes: [...grants].map(([scope, write]) => ({
          scope,
          write: write && !readOnly && !READ_ONLY_API_TOKEN_SCOPES.includes(scope),
        })),
        // Meaningless without a write grant, and sending it anyway would record a
        // permission the token cannot use.
        allowDestructive: hasWrite && allowDestructive,
      },
      {
        onSuccess: (made) => {
          setSecret(made.secret)
          setName('')
          setGrants(new Map())
          setAllowDestructive(false)
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <section className="space-y-4">
      <SectionHeader title="API tokens" description="Tokens that stand in for you when something calls the API." />
      {/* Said in the open rather than in the header's tooltip: what a token can
          reach is the one thing someone has to understand before making one. */}
      <p className="max-w-prose text-sm text-ink-muted">
        A token can only do what you can do, worked out fresh each time it is used — so it never
        outlives your access — and only within the areas you tick below.{' '}
        {worksAtAssignedVenues(user)
          ? 'It reaches the venues you manage, and if those change, so does the token.'
          : 'Revoke one here and it stops immediately.'}
      </p>

      {secret ? <NewToken secret={secret} onDone={() => setSecret(null)} /> : null}

      <form onSubmit={onCreate} className="flex flex-wrap items-end gap-3">
        <Field label="Name" className="min-w-[14rem] flex-1">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What will use it — “Zapier sync”"
            maxLength={80}
          />
        </Field>
        <Field label="Expires" className="w-40">
          <Select value={lifetime} onChange={(e) => onLifetimeChange(e.target.value)}>
            {API_TOKEN_LIFETIMES.map((value) => (
              <option key={value} value={value}>
                {LIFETIME_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(e) => setReadOnly(e.target.checked)}
            className="h-4 w-4 rounded border-line"
          />
          Read-only
        </label>
        <fieldset className="w-full space-y-2 rounded-md border border-line p-3">
          <legend className="px-1 text-sm font-medium">What it may access</legend>
          <p className="text-xs text-ink-muted">
            Tick only what the thing using this token actually needs. Anything unticked answers
            with “forbidden”, so a narrow token is a small loss if it ever leaks.
          </p>
          <div className="grid gap-1 sm:grid-cols-2">
            {offered.map((scope) => {
              const granted = grants.has(scope)
              const risky = HIGH_RISK_API_TOKEN_SCOPES.includes(scope)
              return (
                <div key={scope} className="rounded border border-transparent px-2 py-1.5 hover:border-line">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={granted}
                      onChange={() => toggleScope(scope)}
                      className="mt-1 h-4 w-4 rounded border-line"
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-ink">{SCOPE_LABELS[scope].title}</span>
                        {risky ? <Badge tone="warning">Sensitive</Badge> : null}
                      </span>
                      <span className="block text-xs text-ink-muted">
                        {SCOPE_LABELS[scope].detail}
                      </span>
                    </span>
                  </label>
                  {granted && !readOnly && !READ_ONLY_API_TOKEN_SCOPES.includes(scope) ? (
                    <label className="ml-6 mt-1 flex items-center gap-2 text-xs text-ink-muted">
                      <input
                        type="checkbox"
                        checked={grants.get(scope) === true}
                        onChange={(e) => setWrite(scope, e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-line"
                      />
                      Let it make changes here
                    </label>
                  ) : null}
                </div>
              )
            })}
          </div>
          {hasWrite ? (
            <label className="flex items-start gap-2 border-t border-line pt-2 text-sm">
              <input
                type="checkbox"
                checked={allowDestructive}
                onChange={(e) => setAllowDestructive(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-line"
              />
              <span>
                <span className="font-medium text-ink">Allow bulk deletion</span>
                <span className="block text-xs text-ink-muted">
                  Clearing a venue’s events, wiping imported data, deleting a venue. None of the
                  three can be undone.
                </span>
              </span>
            </label>
          ) : null}
        </fieldset>
        <Button
          type="submit"
          disabled={name.trim() === '' || grants.size === 0}
          className="mb-2"
          pending={create.isPending}
        >
          Create token
        </Button>
        {grants.size === 0 ? (
          <p className="w-full text-xs text-ink-muted">
            Choose at least one area — a token with none can call nothing.
          </p>
        ) : null}
      </form>
      {error ? <FormError>{error}</FormError> : null}

      {renderTokens()}
      <ApiTokenChangesDialog token={changesOf} onClose={() => setChangesOf(null)} />
    </section>
  )

  /**
   * What can be done to one token: revoke it, and only while it is live.
   *
   * Revoking is the only action, and it is destructive — so the row shows no
   * icon at rest, just the menu. A red button on every row of a list is how the
   * page used to read.
   */
  function tokenActions(token: ApiTokenDto): RowAction[] {
    if (token.revokedAt) return []
    return [
      {
        label: 'Revoke token',
        icon: Trash2,
        destructive: true,
        disabled: revoke.isPending,
        onSelect: () =>
          revoke.mutate(token.id, { onError: (e) => setError(requestErrorMessage(e)) }),
      },
    ]
  }

  /**
   * How much a token has changed through the assistant's tools, opening the list.
   * The column is shown only once some token has changed something: until then
   * it would be a column of "None" on every row.
   */
  function renderChanges(token: ApiTokenDto): React.ReactNode {
    if (token.changeCount === 0) return 'None'
    return (
      <button
        type="button"
        className="text-ink underline-offset-2 hover:underline"
        onClick={() => setChangesOf(token)}
      >
        {token.changeCount === 1 ? '1 change' : `${String(token.changeCount)} changes`}
      </button>
    )
  }

  /** Loading, empty and listed, kept out of the JSX rather than nested in it. */
  function renderTokens(): React.ReactElement {
    if (tokensQuery.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>
    // Not "no tokens": a list that did not load says nothing about what is in it.
    if (tokensQuery.isError) {
      return (
        <ErrorState message={requestErrorMessage(tokensQuery.error)} onRetry={() => void tokensQuery.refetch()} />
      )
    }
    if (tokens.length === 0) {
      return <p className="text-sm text-ink-muted">You have no API tokens yet.</p>
    }
    const showChanges = tokens.some((token) => token.changeCount > 0)
    return (
      <Table>
          <TableHeader>
            <TableHead>Name</TableHead>
            <TableHead>Token</TableHead>
            <TableHead>Access</TableHead>
            <TableHead>Reaches</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead>Expires</TableHead>
            {showChanges ? <TableHead>Changes</TableHead> : null}
            <TableActionsHead />
          </TableHeader>
          <TableBody>
            {tokens.map((token) => {
              const state = tokenState(token)
              return (
                <TableRow key={token.id}>
                  <TableCell className="font-medium text-ink">{token.name}</TableCell>
                  <TableCell className="font-mono text-xs text-ink-muted">{token.prefix}…</TableCell>
                  <TableCell>
                    <Badge tone={state.tone}>{state.label}</Badge>
                  </TableCell>
                  <TableCell className="min-w-[9rem] text-xs text-ink-muted">
                    {scopeSummary(token)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">{when(token.lastUsedAt)}</TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">
                    {token.expiresAt ? when(token.expiresAt) : 'Never'}
                  </TableCell>
                  {showChanges ? (
                    <TableCell className="whitespace-nowrap text-ink-muted">{renderChanges(token)}</TableCell>
                  ) : null}
                  <TableActionsCell>
                    <RowActions label={token.name} actions={tokenActions(token)} />
                  </TableActionsCell>
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    )
  }
}
